import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/shared/stores/auth';
import { apiClient } from '@/shared/api/client';

interface StreamState<T> {
  data: T | null;
  connected: boolean;
}

/**
 * הזרם שותק יותר מזה ⇒ תקוע (פרוקסי שאוגר, חיבור חצי-מת). השרת שולח
 * פריים בכל שינוי ו-": ping" כל 10ש, כך ש-25ש של שקט = חיבור מת.
 */
export const STREAM_STALL_MS = 25_000;

/** המתנה בין ניסיונות חיבור אחרי כשל — עולה, ומתאפסת בחיבור מוצלח. */
export const STREAM_RETRY_MS = [1_000, 2_000, 5_000, 10_000, 15_000];

/**
 * Real-time monitor snapshot via Server-Sent Events. Uses a fetch-based reader
 * (so it can send the bearer + tenant headers EventSource can't), parses the
 * "data: {...}\n\n" wire format, and auto-reconnects.
 *
 * 🔑 01/10/2026 (ניטור חי אמיתי): קודם כל כשל אחד העביר את המסך ל-polling עד
 * רענון הדף, וזרם תקוע נראה "מחובר" בלי נתונים. עכשיו:
 *  - watchdog: אין אף בייט STREAM_STALL_MS ⇒ ניתוק וחיבור מחדש.
 *  - כשל ⇒ polling *זמני* בזמן שמנסים שוב את הזרם (backoff), ועוצרים כשחזר.
 *  - הטוקן נקרא בכל חיבור (לא פעם אחת בטעינה).
 */
export function useMonitorStream<T = unknown>(fallbackPath: string, pollMs = 4000): StreamState<T> {
  const [data, setData] = useState<T | null>(null);
  const [connected, setConnected] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    const base = (apiClient.defaults.baseURL ?? '/api/v1/merkaziya').replace(/\/$/, '');

    /** true = הזרם עבד ונסגר (חלון של ~2 דק'); false = נכשל/נתקע. */
    async function runStream(onOpen: () => void): Promise<boolean> {
      const { token, tenantSlug } = useAuthStore.getState();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      let stalled = false;
      let watchdog: number | undefined;
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      const arm = () => {
        window.clearTimeout(watchdog);
        watchdog = window.setTimeout(() => {
          stalled = true;
          ctrl.abort();
          // גם הקורא: abort לבד לא תמיד משחרר read() תלוי (פרוקסי/מימוש).
          void reader?.cancel().catch(() => {});
        }, STREAM_STALL_MS);
      };

      try {
        arm();
        const resp = await fetch(`${base}/admin/monitor/stream`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
            Accept: 'text/event-stream',
          },
          signal: ctrl.signal,
        });
        if (!resp.ok || !resp.body) return false;

        setConnected(true);
        // הזרם חזר — ה-polling הזמני נעצר עכשיו, לא בסוף חלון של 2 דק'.
        onOpen();
        reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done || cancelled) break;
          arm(); // כל בייט — כולל ": ping" — מוכיח שהזרם חי.
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE events (separated by a blank line).
          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = chunk.split('\n').find((l) => l.startsWith('data:'));
            if (line) {
              try {
                const parsed = JSON.parse(line.slice(5).trim());
                if (!cancelled) setData((parsed.data ?? parsed) as T);
              } catch { /* ignore malformed frame */ }
            }
          }
        }
        return !stalled;
      } catch {
        return false;
      } finally {
        window.clearTimeout(watchdog);
        setConnected(false);
      }
    }

    let pollHandle: number | undefined;
    async function poll() {
      try {
        const { data: body } = await apiClient.get(fallbackPath);
        if (!cancelled) setData((body?.data ?? body) as T);
      } catch { /* keep last */ }
    }
    const startPolling = () => {
      if (pollHandle || cancelled) return;
      void poll();
      pollHandle = window.setInterval(poll, pollMs);
    };
    const stopPolling = () => {
      if (pollHandle) { window.clearInterval(pollHandle); pollHandle = undefined; }
    };

    (async () => {
      let failures = 0;
      while (!cancelled) {
        const ok = await runStream(stopPolling);
        if (cancelled) return;
        if (ok) {
          failures = 0;
          stopPolling();
          // החלון של השרת נסגר כרגיל — פותחים מיד את הבא.
          await new Promise((r) => setTimeout(r, 300));
          continue;
        }
        // כשל/תקיעה: המסך ממשיך להתעדכן ב-polling עד שהזרם חוזר.
        startPolling();
        const wait = STREAM_RETRY_MS[Math.min(failures, STREAM_RETRY_MS.length - 1)];
        failures++;
        await new Promise((r) => setTimeout(r, wait));
      }
    })();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackPath, pollMs]);

  return { data, connected };
}
